import { Disclosure } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import cn from 'classnames';
import { createTreatmentKey } from 'libs/end-to-end/src/treatment';
import { isTreatmentStatusSelected } from 'libs/end-to-end/src/treatment';
import React, { Fragment, MutableRefObject, useMemo, useState } from 'react';
import { BIAS_TYPE_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { TREATMENT_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { IconChevronRight24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight24px';
import { IconDelete24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconDelete24px';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';

export enum SelectedTreatmentsVariant {
  CART,
  SIDEBAR,
}

interface SelectedTreatmentsProps {
  variant: SelectedTreatmentsVariant;
  onTreatmentRemove?: (treatmentKey: string) => void;
  onTreatmentActiveToggle?: (treatmentKey: string) => void;
}

export const SelectedTreatments = ({
  variant,
  onTreatmentRemove,
  onTreatmentActiveToggle,
}: SelectedTreatmentsProps) => {
  const language = useLanguage();
  const { myUser } = useUser();

  const { myObservation, removeTreatment, toggleTreatment } = useObservation();

  const { getLevelByColumnValue, getDimensionByColumnHeaderValue } =
    useDimensionConfig();

  // It's necessary to indicate in a state change that a treatment
  // will be removed, to allow its animated transition to complete,
  // prior to actually removing it.
  const [treatmentToRemove, setTreatmentToRemove] = useState<string | null>();

  const onAfterLeave = useAsyncCallback(
    async (
      isAlive,
      treatmentKey: string,
      close: (
        focusableElement?: HTMLElement | MutableRefObject<HTMLElement | null>,
      ) => void,
    ) => {
      const treatments = myObservation?.treatments ?? [];
      const isLastTreatment = treatments.length === 1;

      removeTreatment(treatmentKey, {
        onBeforeRemove: async () => {
          if (isAlive()) {
            setTreatmentToRemove(undefined);
          }
        },
        onRemove: async () => {
          if (!isAlive()) {
            return;
          }

          if (onTreatmentRemove) {
            onTreatmentRemove(treatmentKey);
          }

          if (isLastTreatment) {
            close();
          }
        },
      });
    },
    [myObservation?.treatments, onTreatmentRemove, removeTreatment],
  );

  const onChangeOuter = useAsyncCallback(
    async (isAlive, treatmentKey: string, isActive: boolean) => {
      if (!isAlive()) {
        return;
      }

      if (onTreatmentActiveToggle) {
        onTreatmentActiveToggle(treatmentKey);
        return;
      }

      toggleTreatment(treatmentKey, isActive);
    },
    [onTreatmentActiveToggle, toggleTreatment],
  );

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const listItems = useMemo(() => {
    if (
      !myUser ||
      !myUser.organization ||
      !myObservation ||
      !myObservation.treatments ||
      !myObservation.testSummary
    ) {
      return <></>;
    }

    const selectedTreatments = myObservation.treatments.filter((t) =>
      isTreatmentStatusSelected(t.status),
    );

    const items: React.ReactNode[] = [];
    let isFirstFocusArea = true;

    for (const focusArea of myObservation.focusAreas ?? []) {
      const failedCount = myObservation.testSummary.tests.filter(
        (test) =>
          test.focusArea === focusArea.focusArea && test.status === 'Fail',
      ).length;

      const selectedTreatmentsForFocusArea = selectedTreatments.filter(
        (selectedTreatment) =>
          selectedTreatment.focusArea === focusArea.focusArea,
      );

      const activeTreatmentsForFocusArea =
        selectedTreatmentsForFocusArea.filter(
          (selectedTreatment) => selectedTreatment.status === 'Active',
        );

      const isSidebar = variant === SelectedTreatmentsVariant.SIDEBAR;
      const isCart = variant === SelectedTreatmentsVariant.CART;

      const focusAreaName = focusArea.displayName;

      items.push(
        <li key={focusArea.focusArea} data-qa={focusAreaName}>
          <Disclosure
            defaultOpen={isSidebar && selectedTreatmentsForFocusArea.length > 0}
          >
            {({ open, close }) => {
              const treatmentListItems: React.ReactNode[] = [];

              for (const selectedTreatment of selectedTreatmentsForFocusArea) {
                const treatmentKey = createTreatmentKey(selectedTreatment);
                const afterLeave = () => onAfterLeave(treatmentKey, close);
                const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangeOuter(treatmentKey, e.target.checked);

                const levelDefinition = getLevelByColumnValue(
                  selectedTreatment.dimension,
                  selectedTreatment.level,
                );

                const dimensionDefinition = getDimensionByColumnHeaderValue(
                  selectedTreatment.dimension,
                );

                treatmentListItems.push(
                  <Transition
                    show={
                      !treatmentToRemove || treatmentToRemove !== treatmentKey
                    }
                    key={treatmentKey}
                    as={Fragment}
                    leave="transition duration-200 ease-in"
                    leaveFrom="transform opacity-100"
                    leaveTo="transform opacity-0"
                    afterLeave={afterLeave}
                  >
                    <li
                      className={cn('flex items-center my-4')}
                      data-qa="treatmentName"
                    >
                      {isSidebar && (
                        <input
                          type="checkbox"
                          data-qa="activateButton"
                          checked={selectedTreatment.status === 'Active'}
                          disabled={false}
                          onChange={onChange}
                          className={cn(
                            'form-checkbox rounded-sm border-0 w-4 h-4 text-white bg-white ring ring-offset-2 ring-transparent ring-offset-white mr-6 disabled:bg-gray-400 disabled:ring-offset-gray-400 disabled:text-gray-400',
                            {
                              'dark-checkmark': isSidebar,
                            },
                          )}
                        />
                      )}
                      <div className="flex-grow">
                        <p
                          data-qa="treatmentTitle"
                          className={cn('text-sm', {
                            'text-gray-1000': isCart,
                            'text-white': isSidebar,
                          })}
                        >
                          {language[
                            TREATMENT_MESSAGE_IDS[selectedTreatment.type]
                          ]()}
                        </p>
                        <p
                          data-qa="treatmentBlurb"
                          className={cn('text-xs ', {
                            'text-gray-800': isCart,
                            'text-gray-200': isSidebar,
                          })}
                        >
                          {dimensionDefinition?.displayName ?? ''}
                          {': '}
                          {levelDefinition?.displayName ?? ''}
                          {' • '}
                          {language[
                            BIAS_TYPE_MESSAGE_IDS[selectedTreatment.biasType]
                          ]()}
                          {` ${language
                            .bias({
                              calibrationFraming:
                                myObservation.calibrationFraming,
                            })
                            .toLowerCase()}`}
                        </p>
                      </div>
                      {variant === SelectedTreatmentsVariant.CART && (
                        <button
                          className="w-5 relative -right-1 text-secondary"
                          onClick={() => setTreatmentToRemove(treatmentKey)}
                        >
                          <div title={language.treatmentCartRemoveTooltip()}>
                            <IconDelete24pxSvg data-qa="deleteIcon" />
                          </div>
                        </button>
                      )}
                    </li>
                  </Transition>,
                );
              }

              return (
                <div>
                  <Disclosure.Button
                    disabled={
                      selectedTreatmentsForFocusArea === undefined ||
                      selectedTreatmentsForFocusArea.length === 0
                    }
                    title={
                      open
                        ? language.treatmentCartCollapseTooltip()
                        : language.treatmentCartExpandTooltip()
                    }
                    className={cn(
                      'flex items-center content-start w-full text-left',
                      {
                        'border-t-1': !isFirstFocusArea,
                        'border-gray-900 border-opacity-40': isCart,
                        'border-white border-opacity-30': isSidebar,
                        'cursor-default': !selectedTreatmentsForFocusArea,
                      },
                    )}
                  >
                    <div className="flex-grow py-4">
                      <p
                        className={cn('text-base font-sans', {
                          'text-gray-1000': isCart,
                          'text-white': isSidebar,
                        })}
                      >
                        {focusAreaName}
                      </p>
                      <p
                        data-qa="focusAreaResultsAndSelectedSolutions"
                        className={cn('text-xs', {
                          'text-gray-800': isCart,
                          'text-gray-200': isSidebar,
                        })}
                      >
                        {language.testsFailed({
                          failedTestsCount: failedCount ?? 0,
                        })}{' '}
                        •{' '}
                        {isSidebar
                          ? activeTreatmentsForFocusArea.length === 1
                            ? language.treatmentsActiveCountSingular({
                                count: activeTreatmentsForFocusArea.length,
                              })
                            : language.treatmentsActiveCountPlural({
                                count: activeTreatmentsForFocusArea.length,
                              })
                          : selectedTreatmentsForFocusArea.length === 1
                            ? language.treatmentSelectionCountSingular({
                                count: selectedTreatmentsForFocusArea.length,
                              })
                            : language.treatmentSelectionCountPlural({
                                count: selectedTreatmentsForFocusArea.length,
                              })}
                      </p>
                    </div>
                    <div>
                      <IconChevronRight24pxSvg
                        svg={{
                          style: { transformOrigin: 'center' },
                          className: cn(
                            'w-3 relative top-2 transition-transform',
                            {
                              'text-secondary':
                                selectedTreatmentsForFocusArea?.length &&
                                isCart,
                              'text-gray-300':
                                !selectedTreatmentsForFocusArea?.length &&
                                isCart,
                              'text-white':
                                selectedTreatmentsForFocusArea?.length &&
                                isSidebar,
                              'text-white text-opacity-25':
                                !selectedTreatmentsForFocusArea?.length &&
                                isSidebar,
                              'rotate-90': !open,
                              '-rotate-90': open,
                            },
                          ),
                        }}
                      />
                    </div>
                  </Disclosure.Button>
                  <Transition show={open}>
                    <Disclosure.Panel
                      static
                      className={cn('bg-gray-100 -mx-6 flow-root', {
                        'bg-gray-100': isCart,
                        'bg-gray-1000': isSidebar,
                      })}
                    >
                      <Transition.Child
                        enter="transition duration-500 ease-out"
                        enterFrom="transform opacity-0"
                        enterTo="transform opacity-100"
                        leave="transition duration-200 ease-in"
                        leaveFrom="transform opacity-100"
                        leaveTo="transform opacity-0"
                      >
                        <ul className="px-6">{treatmentListItems}</ul>
                      </Transition.Child>
                    </Disclosure.Panel>
                  </Transition>
                </div>
              );
            }}
          </Disclosure>
        </li>,
      );

      isFirstFocusArea = false;
    }

    return items;
  }, [
    myUser,
    myObservation,
    language,
    variant,
    getLevelByColumnValue,
    getDimensionByColumnHeaderValue,
    treatmentToRemove,
    onAfterLeave,
    onChangeOuter,
  ]);

  return <ul className="mt-4">{listItems}</ul>;
};
